import React, {useCallback} from 'react';


import {Skeleton} from 'src/components/skeleton';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';

import {CheckBox} from '@pb/kit-component-checkbox';
import {FormSection} from '@pb/kit-component-form-section';

import {NextLoginActionsSkeleton} from './next-login-actions.skeleton';

import type {Dispatch, SetStateAction} from 'react';
import type {UserLoginStateResponse} from 'src/openapi/admin/models/UserLoginStateResponse';
import type {CheckBoxChangeData} from 'src/typedef';

type Props = {
    isLoading: boolean,
    userLoginPreconditions: UserLoginStateResponse,
    userMfaEnforced: boolean,
    setUserMfaEnforced: Dispatch<SetStateAction<boolean>>,
    isEmailPrimaryMfaEnabled: boolean,
    emailPrimaryFactor: boolean,
    setEmailPrimaryFactor: Dispatch<SetStateAction<boolean>>,
    onChange: Dispatch<SetStateAction<UserLoginStateResponse>>,
    disabled: boolean,
    onFieldChange: (parameterName: string) => void,
};

const CHECKBOX_LABEL = 'Multi-Factor Authentication (MFA)';
const EMAIL_PRIMARY_FACTOR_CHECKBOX_LABEL = 'Email as Primary MFA';

export const NextLoginActions = ({
    isLoading,
    userLoginPreconditions,
    setUserMfaEnforced,
    isEmailPrimaryMfaEnabled,
    emailPrimaryFactor,
    setEmailPrimaryFactor,
    onChange,
    disabled,
    onFieldChange,
    userMfaEnforced,
}: Props) => {
    const {hasAccess} = useAdminAccessRight();

    const setMustChangePassword = useCallback((changeData: CheckBoxChangeData) => {
        const checked = changeData.event.currentTarget.checked;
        onFieldChange('User must change password');
        onChange(prevState => ({
            ...prevState,
            [changeData.name]: checked,
        }));
    }, [onFieldChange, onChange]);

    const setUserMfaEnforcedHandler = useCallback((changeData: CheckBoxChangeData) => {
        const mfaEnforced = changeData.event.currentTarget.checked;
        onFieldChange('User MFA enforced');
        setUserMfaEnforced(() => mfaEnforced);

        if (isEmailPrimaryMfaEnabled && !mfaEnforced) {
            onFieldChange('Email as Primary MFA');
            setEmailPrimaryFactor(false);
        }
    }, [isEmailPrimaryMfaEnabled, onFieldChange, setEmailPrimaryFactor, setUserMfaEnforced]);

    const setEmailPrimaryFactorHandler = useCallback((changeData: CheckBoxChangeData) => {
        const checked = changeData.event.currentTarget.checked;
        onFieldChange('Email as Primary MFA');
        setEmailPrimaryFactor(checked);
    }, [onFieldChange, setEmailPrimaryFactor]);

    const mustChangePasswordDisabled = disabled || userLoginPreconditions.ssoMandatory;
    const enforceMfaDisabled = !hasAccess(ACCESS_RIGHTS.USER_MFA_MANAGEMENT);
    const emailPrimaryFactorDisabled = enforceMfaDisabled || !userMfaEnforced;

    return (
        <Skeleton shown={isLoading} view={<NextLoginActionsSkeleton/>}>
            <FormSection headline="Next Login Actions">
                <CheckBox
                    disabled={mustChangePasswordDisabled}
                    name="mustChangePassword"
                    label="User must change password"
                    checked={userLoginPreconditions.mustChangePassword}
                    onCheckBoxChange={setMustChangePassword}
                />
                <div>
                    <CheckBox
                        disabled={enforceMfaDisabled}
                        name="enforceMfa"
                        label={CHECKBOX_LABEL}
                        checked={userMfaEnforced}
                        onCheckBoxChange={setUserMfaEnforcedHandler}
                    />
                    {isEmailPrimaryMfaEnabled ? (
                        <CheckBox
                            disabled={emailPrimaryFactorDisabled}
                            name="emailPrimaryFactor"
                            label={EMAIL_PRIMARY_FACTOR_CHECKBOX_LABEL}
                            checked={emailPrimaryFactor}
                            onCheckBoxChange={setEmailPrimaryFactorHandler}
                        />
                    ) : null}
                </div>
            </FormSection>
        </Skeleton>
    );
};
